import { useQuery } from '@tanstack/react-query';

import { audit } from '../api/client';
import { formatTsFull } from '../lib/format';
import { Badge, EmptyState, ErrorState, Spinner } from '../components/ui';

/** Severity hint of one audit action — colour follows the same semantics. */
function toneOf(action: string): 'bad' | 'warn' | 'ok' | 'neutral' {
  if (action.includes('nack') || action.includes('timeout') || action.includes('removed')) return 'bad';
  if (action.includes('enqueue') || action.includes('run-backup') || action.includes('added')) return 'ok';
  if (action.includes('ingest')) return 'neutral';
  return 'neutral';
}

/** `/audit` — the last 100 records of `bm:audit` (sweeps, collector, runner, admins). */
export function AuditPage() {
  const query = useQuery({ queryKey: ['audit'], queryFn: ({ signal }) => audit.get(signal), refetchInterval: 60_000 });

  if (query.isPending) return <Spinner label="Загружаю аудит" />;
  if (query.isError) return <ErrorState message={String(query.error)} onRetry={() => void query.refetch()} />;

  const items = query.data?.items ?? [];
  return (
    <>
      <div className="page-header">
        <div>
          <h1>Аудит</h1>
          <p className="page-header__sub">
            Последние {items.length} записей <code>bm:audit</code> (TTL 180 дней, журнал лучший-усилие).
          </p>
        </div>
      </div>

      <section className="card card--flush">
        {items.length === 0 ? (
          <EmptyState title="Аудит пуст" hint="Записи появляются при первом событии: ingest, enqueue, изменение админов." />
        ) : (
          <div className="table-wrap">
            <table className="data" data-testid="audit-table">
              <thead>
                <tr>
                  <th>Когда</th>
                  <th>Кто</th>
                  <th>Действие</th>
                  <th>Детали</th>
                </tr>
              </thead>
              <tbody>
                {items.map((entry, index) => (
                  <tr key={`${entry.ts}-${index}`}>
                    <td className="cell-muted num" style={{ textAlign: 'left' }}>
                      {formatTsFull(new Date(entry.ts).toISOString())}
                    </td>
                    <td className="cell-muted">{entry.actor}</td>
                    <td>
                      <Badge tone={toneOf(entry.action)}>{entry.action}</Badge>
                    </td>
                    <td className="cell-muted" style={{ maxWidth: 480 }}>
                      <code style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{JSON.stringify(entry.detail)}</code>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </>
  );
}
